import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Globe,
  Loader2,
  Plus,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { useToast } from "@/components/ui/Toast";
import { useConfirm } from "@/components/ui/Confirm";
import { useShell } from "@/components/Shell";
import { SettingsLayout, FormSection } from "@/routes/Settings/SettingsLayout";
import {
  getOverview,
  updateSettings,
  type Campground,
} from "@/lib/api";
import { absolute, relative } from "@/lib/format";
import { cn } from "@/lib/cn";

/**
 * Domains settings page.
 *
 * Manages the campground.allowed_domains allowlist (CRUD) and surfaces
 * the recent-origin-rejections ring buffer maintained by the chat edge
 * function. Every mutation is auto-saved against the server with an
 * optimistic local update and rollback on failure.
 */

// ---------------------------------------------------------------------------
// Validation helper. Keeps the rules cheap so they can live in this file:
//   - lowercase host (a-z, 0-9, dot, hyphen)
//   - optional leading "*." wildcard
//   - no scheme, no path, no trailing slash
//   - 1..253 chars
// Strips a leading http(s):// and trailing slash before checking.
// ---------------------------------------------------------------------------
export function validateDomain(
  s: string,
): { ok: true; value: string } | { ok: false; reason: string } {
  if (!s) return { ok: false, reason: "Enter a domain." };
  let v = s.trim().toLowerCase();
  v = v.replace(/^https?:\/\//, "");
  v = v.replace(/\/+$/, "");
  if (!v) return { ok: false, reason: "Enter a domain." };
  if (v.includes("/")) return { ok: false, reason: "Remove the path — host only." };
  if (v.includes(" ")) return { ok: false, reason: "Domains can't contain spaces." };
  if (v.length > 253) return { ok: false, reason: "Domain is too long." };

  // Wildcard prefix is optional and only allowed once, at the very start.
  let host = v;
  if (host.startsWith("*.")) host = host.slice(2);

  if (!host) return { ok: false, reason: "Add a host after the wildcard." };
  if (!/^[a-z0-9.-]+$/.test(host)) {
    return { ok: false, reason: "Only letters, digits, dots and hyphens are allowed." };
  }
  if (host.startsWith(".") || host.endsWith(".")) {
    return { ok: false, reason: "Domain can't start or end with a dot." };
  }
  if (host.startsWith("-") || host.endsWith("-")) {
    return { ok: false, reason: "Domain can't start or end with a hyphen." };
  }
  if (!host.includes(".")) {
    return { ok: false, reason: "Use a full domain like yourcampground.com." };
  }
  // Each label must be non-empty and 1..63 chars.
  for (const label of host.split(".")) {
    if (!label) return { ok: false, reason: "Empty label between dots." };
    if (label.length > 63) return { ok: false, reason: "Each label must be 63 chars or fewer." };
  }

  return { ok: true, value: v };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type SaveState = "idle" | "saving" | "saved" | "error";

export function Domains() {
  const toast = useToast();
  const confirm = useConfirm();
  const { refresh: refreshShell } = useShell();

  const [campground, setCampground] = useState<Campground | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getOverview();
      setCampground(r.campground);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const domains = useMemo(
    () => campground?.allowed_domains ?? [],
    [campground],
  );
  const rejections = useMemo(
    () => campground?.widget_config?.recent_origin_rejections ?? [],
    [campground],
  );

  // -------------------------------------------------------------------------
  // Save helper. Optimistically replaces the local list, awaits the server,
  // rolls back + toasts on failure. Returns a boolean so callers can clear
  // the add-input only on success.
  // -------------------------------------------------------------------------
  async function commit(nextDomains: string[]): Promise<boolean> {
    if (!campground) return false;
    const prev = campground;
    setCampground({ ...campground, allowed_domains: nextDomains });
    setSaveState("saving");
    try {
      const r = await updateSettings({ allowed_domains: nextDomains });
      setCampground(r.campground);
      setSaveState("saved");
      // Reset the indicator back to idle after a moment so it doesn't loiter.
      setTimeout(() => {
        setSaveState((s) => (s === "saved" ? "idle" : s));
      }, 1800);
      // Keep the shell-level home payload in sync so the bot config snapshot
      // on the home page reflects the change immediately.
      void refreshShell();
      return true;
    } catch (e) {
      setCampground(prev);
      setSaveState("error");
      toast.push(e instanceof Error ? e.message : "Couldn't save domains.", "error");
      return false;
    }
  }

  async function onAdd(raw: string) {
    const v = validateDomain(raw);
    if (!v.ok) {
      setDraftError(v.reason);
      return;
    }
    if (domains.includes(v.value)) {
      setDraftError("That domain is already in the list.");
      return;
    }
    setDraftError(null);
    const ok = await commit([...domains, v.value]);
    if (ok) setDraft("");
  }

  async function onRemove(d: string) {
    const ok = await confirm({
      title: "Remove this domain?",
      description: (
        <p>
          The widget will stop loading on <code className="rounded bg-surface-3 px-1 py-0.5 text-xs">{d}</code>. Add it back any time.
        </p>
      ),
      confirmLabel: "Remove",
      variant: "danger",
    });
    if (!ok) return;
    await commit(domains.filter((x) => x !== d));
  }

  async function onAllowOrigin(origin: string) {
    // Strip scheme and trailing slash from the rejected origin before
    // pushing it into the allowlist. validateDomain() will normalise
    // anything unexpected; on the off chance the origin isn't a valid
    // hostname we surface the error inline.
    const v = validateDomain(origin);
    if (!v.ok) {
      toast.push(`Couldn't add ${origin}: ${v.reason}`, "error");
      return;
    }
    if (domains.includes(v.value)) {
      toast.push("That domain is already allowed.");
      return;
    }
    await commit([...domains, v.value]);
  }

  // -------------------------------------------------------------------------
  // Error fallback
  // -------------------------------------------------------------------------
  if (error && !campground) {
    return (
      <SettingsLayout
        title="Allowed domains"
        description="The widget will only load on websites you list here. The chat endpoint rejects any request whose Origin header isn't in this list."
      >
        <Card className="border-danger/30">
          <div className="flex items-start gap-3 px-5 py-4">
            <AlertCircle size={18} strokeWidth={1.75} className="mt-0.5 shrink-0 text-danger" />
            <div className="flex-1">
              <p className="text-sm font-medium text-text-strong">Couldn't load domains</p>
              <p className="mt-1 text-xs text-text-soft">{error}</p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        </Card>
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout
      title="Allowed domains"
      description="The widget will only load on websites you list here. The chat endpoint rejects any request whose Origin header isn't in this list."
    >
      {/* ===================================================================
          1. Allowed domains
      =================================================================== */}
      <section className="card">
        <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
          <div>
            <h2 className="text-md font-medium text-text-strong">Allowed domains</h2>
            <p className="mt-0.5 text-xs text-text-soft">
              Add every domain where you embed the widget. Changes save automatically.
            </p>
          </div>
          <SaveIndicator state={saveState} />
        </header>

        {/* Add row */}
        <div className="px-5 py-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void onAdd(draft);
            }}
            className="flex flex-col gap-2 sm:flex-row"
          >
            <Input
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                if (draftError) setDraftError(null);
              }}
              placeholder="yourcampground.com"
              aria-label="Domain to add"
              aria-invalid={draftError ? true : undefined}
              autoComplete="off"
              spellCheck={false}
              disabled={loading}
            />
            <Button
              type="submit"
              variant="primary"
              iconLeft={<Plus size={14} strokeWidth={1.75} />}
              disabled={loading || !draft.trim()}
            >
              Add domain
            </Button>
          </form>
          {draftError && (
            <p className="mt-1.5 flex items-center gap-1 text-xs text-danger">
              <AlertCircle size={12} strokeWidth={1.75} />
              {draftError}
            </p>
          )}
          <p className="mt-1.5 text-xs text-text-faint">
            Host only — no <span className="font-mono">https://</span>, no paths. Use{" "}
            <span className="font-mono">*.yourcampground.com</span> to allow subdomains.
          </p>
        </div>

        {/* List */}
        <div className="border-t border-line">
          {loading && !campground ? (
            <DomainListSkeleton />
          ) : domains.length === 0 ? (
            <div className="px-5 py-6">
              <EmptyState
                icon={Globe}
                title="No domains allowed yet"
                description="Your widget won't load anywhere until you add one."
              />
            </div>
          ) : (
            <ul className="divide-y divide-line">
              {domains.map((d) => (
                <li
                  key={d}
                  className="flex items-center justify-between gap-3 px-5 py-3"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate font-mono text-sm text-text-strong">{d}</span>
                    <a
                      href={`https://${d.replace(/^\*\./, "")}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex h-7 w-7 items-center justify-center rounded text-text-soft hover:bg-surface-3 hover:text-text-strong"
                      aria-label={`Open ${d} in a new tab`}
                      title={`Open ${d} in a new tab`}
                    >
                      <ExternalLink size={13} strokeWidth={1.75} />
                    </a>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft={<Trash2 size={12} strokeWidth={1.75} />}
                    onClick={() => void onRemove(d)}
                    aria-label={`Remove ${d}`}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ===================================================================
          2. Recently rejected origins
      =================================================================== */}
      <FormSection
        title="Recently rejected origins"
        description="Origins that hit the chat endpoint but weren't on the allowlist. Useful for debugging widget installs."
      >
        <div className="px-5 py-4">
          {loading && !campground ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : rejections.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title="No rejections recorded"
              description="Origins that try to use your widget but aren't allowlisted will appear here for debugging. Most likely you'll never see anything."
            />
          ) : (
            <div className="overflow-x-auto rounded border border-line">
              <div className="grid min-w-[480px] grid-cols-[1fr_auto_auto_auto] gap-x-4 border-b border-line bg-surface-2 px-3 py-2 text-2xs uppercase tracking-[0.12em] text-text-faint">
                <div>Origin</div>
                <div>Last seen</div>
                <div className="text-right">Count</div>
                <div />
              </div>
              <ul className="min-w-[480px] divide-y divide-line">
                {rejections.map((r) => {
                  const host = r.origin.replace(/^https?:\/\//, "").replace(/\/+$/, "");
                  const alreadyAllowed = domains.includes(host.toLowerCase());
                  return (
                    <li
                      key={r.origin}
                      className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-4 px-3 py-2.5 text-sm"
                    >
                      <span className="truncate font-mono text-text-strong">{r.origin}</span>
                      <span
                        className="text-xs text-text-soft"
                        title={absolute(r.last_seen_at)}
                      >
                        {relative(r.last_seen_at)}
                      </span>
                      <span className="text-right tabular-nums text-text">{r.count}</span>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={alreadyAllowed}
                        onClick={() => void onAllowOrigin(r.origin)}
                        title={alreadyAllowed ? "Already in the allowlist" : undefined}
                      >
                        {alreadyAllowed ? "Allowed" : "Add to allowlist"}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </FormSection>

      {/* ===================================================================
          3. Wildcard / staging help
      =================================================================== */}
      <FormSection title="Need wildcards or staging?">
        <div className="space-y-3 px-5 py-4 text-sm text-text">
          <p>
            Subdomains aren't matched automatically. If your widget lives on both{" "}
            <span className="font-mono text-text-strong">yourcampground.com</span> and{" "}
            <span className="font-mono text-text-strong">www.yourcampground.com</span>, add both.
          </p>
          <p>
            Use a wildcard like{" "}
            <span className="font-mono text-text-strong">*.yourcampground.com</span> to allow any
            single-level subdomain (for example{" "}
            <span className="font-mono">staging.yourcampground.com</span> or{" "}
            <span className="font-mono">www.yourcampground.com</span>). The wildcard only matches
            the leftmost label — deeper paths like{" "}
            <span className="font-mono">a.b.yourcampground.com</span> still need their own entry.
          </p>
          <p className="text-text-soft">
            Staging environments and preview deployments count as separate origins, so add their
            hostnames here while you're testing.
          </p>
        </div>
      </FormSection>
    </SettingsLayout>
  );
}

// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------

function SaveIndicator({ state }: { state: SaveState }) {
  if (state === "idle") return null;
  if (state === "saving") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-text-soft">
        <Loader2 size={12} className="animate-spin" />
        Saving…
      </span>
    );
  }
  if (state === "saved") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-success">
        <CheckCircle2 size={12} strokeWidth={1.75} />
        Saved
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-danger">
      <AlertCircle size={12} strokeWidth={1.75} />
      Save failed
    </span>
  );
}

function DomainListSkeleton() {
  return (
    <ul className="divide-y divide-line">
      {[0, 1, 2].map((i) => (
        <li key={i} className={cn("flex items-center justify-between gap-3 px-5 py-3")}>
          <Skeleton className="h-4 w-56" />
          <Skeleton className="h-7 w-20" />
        </li>
      ))}
    </ul>
  );
}

export default Domains;
